import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useInfoGrants } from './useInfoGrants';
import { useAuth } from '@/components/AuthProvider';
import { supabase } from '@/integrations/supabase/client';

interface VisibilitySettings {
  use_global_defaults?: boolean;
  hide_price?: boolean;
  hide_location?: boolean;
  hide_serial?: boolean;
  hide_hours?: boolean;
  hide_contact?: boolean;
  hide_email?: boolean;
  hide_phone?: boolean;
  hide_company?: boolean;
  hide_modules?: boolean;
  hide_shipping?: boolean;
}

interface OwnerPreferences {
  show_email: boolean;
  show_phone: boolean;
  show_location: boolean;
  show_company: boolean;
}

interface EffectiveVisibility {
  loading: boolean;
  // Can see (unblurred) - true means user CAN see this field
  canSeePrice: boolean;
  canSeeLocation: boolean;
  canSeeHours: boolean;
  canSeeSerial: boolean;
  canSeeContact: boolean;
  canSeeModules: boolean;
  canSeeShipping: boolean;
  // Should blur (hide) - true means field SHOULD be blurred
  blurPrice: boolean;
  blurLocation: boolean;
  blurHours: boolean;
  blurSerial: boolean;
  blurContact: boolean;
  blurModules: boolean;
  blurShipping: boolean;
}

/**
 * Fetches a listing owner's global privacy preferences from user_preferences.
 * Only enabled when use_global_defaults is true (or not set).
 */
const useOwnerPreferences = (ownerId?: string, enabled: boolean = false) => {
  return useQuery({
    queryKey: ['owner-preferences', ownerId],
    queryFn: async (): Promise<OwnerPreferences> => {
      if (!ownerId) {
        return { show_email: true, show_phone: true, show_location: true, show_company: true };
      }

      const { data, error } = await supabase
        .from('user_preferences')
        .select('show_email, show_phone, show_location, show_company')
        .eq('user_id', ownerId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching owner preferences:', error);
        return { show_email: true, show_phone: true, show_location: true, show_company: true };
      }

      // If no preferences row exists, default to showing everything
      if (!data) {
        return { show_email: true, show_phone: true, show_location: true, show_company: true };
      }

      return {
        show_email: data.show_email ?? true,
        show_phone: data.show_phone ?? true,
        show_location: data.show_location ?? true,
        show_company: data.show_company ?? true,
      };
    },
    enabled: enabled && !!ownerId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000,
  });
};

/**
 * Hook that computes effective visibility for listing fields
 * considering owner status, global preferences, and info access grants
 */
export const useEffectiveVisibility = (
  listingId?: string,
  visibilitySettings?: VisibilitySettings | null,
  ownerId?: string
): EffectiveVisibility => {
  const { user } = useAuth();
  const { 
    loading: grantsLoading,
    hasPriceAccess,
    hasLocationAccess,
    hasHoursAccess,
    hasSerialAccess,
    hasEmailAccess,
    hasPhoneAccess,
    hasCompanyAccess,
    hasModulesAccess,
    hasShippingAccess,
  } = useInfoGrants(listingId);

  const isOwner = user?.id === ownerId;
  const useGlobalDefaults = visibilitySettings?.use_global_defaults ?? true;

  // Fetch owner's global preferences when use_global_defaults is true
  const { data: ownerPrefs, isLoading: prefsLoading } = useOwnerPreferences(
    ownerId,
    useGlobalDefaults && !isOwner
  );

  return useMemo(() => {
    // Determine what the listing hides based on settings source
    let listingHidesPrice: boolean;
    let listingHidesLocation: boolean;
    let listingHidesHours: boolean;
    let listingHidesSerial: boolean;
    let listingHidesContact: boolean;
    let listingHidesModules: boolean;
    let listingHidesShipping: boolean;

    if (useGlobalDefaults && ownerPrefs) {
      // Using global defaults: apply owner's account-level preferences
      // Price, serial, hours, modules, shipping are per-listing only (not in global prefs)
      listingHidesPrice = false;
      listingHidesLocation = ownerPrefs.show_location === false;
      listingHidesHours = false;
      listingHidesSerial = false;
      listingHidesContact = ownerPrefs.show_email === false || ownerPrefs.show_phone === false || ownerPrefs.show_company === false;
      listingHidesModules = false;
      listingHidesShipping = false;
    } else if (!useGlobalDefaults) {
      // Per-listing overrides: use the listing's own visibility_settings
      listingHidesPrice = visibilitySettings?.hide_price === true;
      listingHidesLocation = visibilitySettings?.hide_location === true;
      listingHidesHours = visibilitySettings?.hide_hours === true;
      listingHidesSerial = visibilitySettings?.hide_serial === true;
      listingHidesContact = visibilitySettings?.hide_contact === true;
      listingHidesModules = visibilitySettings?.hide_modules === true;
      listingHidesShipping = visibilitySettings?.hide_shipping === true;
    } else {
      // Global defaults but prefs not loaded yet — don't hide anything until we know
      listingHidesPrice = false;
      listingHidesLocation = false;
      listingHidesHours = false;
      listingHidesSerial = false;
      listingHidesContact = false;
      listingHidesModules = false;
      listingHidesShipping = false;
    }

    // User can see if: owner OR has grant for that field
    const canSeePrice = isOwner || hasPriceAccess();
    const canSeeLocation = isOwner || hasLocationAccess();
    const canSeeHours = isOwner || hasHoursAccess();
    const canSeeSerial = isOwner || hasSerialAccess();
    const canSeeContact = isOwner || hasEmailAccess() || hasPhoneAccess() || hasCompanyAccess();
    const canSeeModules = isOwner || hasModulesAccess();
    const canSeeShipping = isOwner || hasShippingAccess();

    // Blur if: listing hides it AND user cannot see it
    const blurPrice = Boolean(listingHidesPrice && !canSeePrice);
    const blurLocation = Boolean(listingHidesLocation && !canSeeLocation);
    const blurHours = Boolean(listingHidesHours && !canSeeHours);
    const blurSerial = Boolean(listingHidesSerial && !canSeeSerial);
    const blurContact = Boolean(listingHidesContact && !canSeeContact);
    const blurModules = Boolean(listingHidesModules && !canSeeModules);
    const blurShipping = Boolean(listingHidesShipping && !canSeeShipping);

    return {
      loading: grantsLoading || (useGlobalDefaults && !isOwner && prefsLoading),
      canSeePrice,
      canSeeLocation,
      canSeeHours,
      canSeeSerial,
      canSeeContact,
      canSeeModules,
      canSeeShipping,
      blurPrice,
      blurLocation,
      blurHours,
      blurSerial,
      blurContact,
      blurModules,
      blurShipping,
    };
  }, [
    isOwner,
    useGlobalDefaults,
    visibilitySettings,
    ownerPrefs,
    grantsLoading,
    prefsLoading,
    hasPriceAccess,
    hasLocationAccess,
    hasHoursAccess,
    hasSerialAccess,
    hasEmailAccess,
    hasPhoneAccess,
    hasCompanyAccess,
    hasModulesAccess,
    hasShippingAccess,
  ]);
};
